import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { ApiError, api, type TermsAsked } from "./api.ts";
import { ConfirmDialog } from "./Dialog.tsx";
import { ReportThis } from "./ReportThis.tsx";
import { Button, Icon } from "./souvenir.ts";
import { Wordmark } from "./ui.tsx";
import { PRIVACY_URL, TERMS_URL } from "./YourData.tsx";

// 16 or older, and the Terms, once (features/getting-started.md; src/auth/terms.ts): the one screen right after a
// first sign-in, before anything else of Tripmo opens, wherever it was opened (Your trips, a trip, a join link, the
// profile, an AI's connection at /welcome). Two ticks, both needed: "I'm 16 or older" and "I agree to the Terms and
// the Privacy Policy" (each linked, in a new tab); Continue, on once both are ticked; and "I'm under 16", which asks
// once more and then removes the account at once. When the Terms changed since the person agreed, it says what
// changed and asks again. Getting started's frame: the wordmark and Sign out, always there.
export function BeforeWeStart({
  asked,
  onAgreed,
  onChanged,
}: {
  asked: TermsAsked;
  // Both ticked and kept: on to where the page was going (it may leave the page, for an AI's allow page).
  onAgreed: () => Promise<void>;
  // The Terms changed while the screen was open: ask again, as they are now.
  onChanged: () => void;
}) {
  const [sixteen, setSixteen] = useState(false);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [under16, setUnder16] = useState<{ busy: boolean; error: string | null } | null>(null);
  const changed = asked.changes.length > 0;
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const before = document.title;
    document.title = changed ? "Our Terms have changed · Tripmo" : "Before we start · Tripmo";
    // Read out where the person is looking: a page they opened shows this instead.
    heading.current?.focus();
    return () => {
      document.title = before;
    };
  }, [changed]);

  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.agreeToTerms(asked.version);
      await onAgreed();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) return onChanged();
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const remove = async () => {
    setUnder16({ busy: true, error: null });
    try {
      await api.removeUnder16();
    } catch (e) {
      setUnder16({ busy: false, error: (e as Error).message });
    }
  };

  return (
    <div className="tm-page tm-start-page">
      <header className="tm-bar">
        <Wordmark />
        <span className="tm-grow" />
        <Button variant="quiet" size="sm" onClick={() => api.signOut()}>
          Sign out
        </Button>
      </header>
      <main className="tm-terms">
        <h1 className="display-l" ref={heading} tabIndex={-1}>
          {changed ? "Our Terms have changed" : "One thing before we start"}
        </h1>
        {changed && (
          <ul className="tm-terms-changes" aria-label="What's changed">
            {asked.changes.map((change) => (
              <li key={change} className="body">
                {change}
              </li>
            ))}
          </ul>
        )}
        <fieldset className="tm-terms-ticks" aria-label="Before we start">
          <Tick checked={sixteen} onChange={setSixteen}>
            I'm 16 or older
          </Tick>
          <Tick checked={agree} onChange={setAgree}>
            I agree to the{" "}
            <a className="tm-link" href={TERMS_URL} target="_blank" rel="noopener">
              Terms
            </a>{" "}
            and the{" "}
            <a className="tm-link" href={PRIVACY_URL} target="_blank" rel="noopener">
              Privacy Policy
            </a>
          </Tick>
        </fieldset>
        <div className="tm-terms-foot">
          <Button variant="primary" block disabled={!sixteen || !agree || busy} onClick={go}>
            Continue
          </Button>
          <Button variant="quiet" block disabled={busy} onClick={() => setUnder16({ busy: false, error: null })}>
            I'm under 16
          </Button>
        </div>
        {error && (
          <p className="callout tm-error" role="alert">
            {error}
            <ReportThis />
          </p>
        )}
      </main>
      {under16 && (
        <ConfirmDialog
          title="Tripmo is for people 16 and older"
          action="Remove my account"
          doing="Removing…"
          busy={under16.busy}
          error={under16.error}
          onConfirm={remove}
          onCancel={() => setUnder16(null)}
        >
          <p className="body">Your account will be removed now, and you'll be signed out. This can't be undone.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

// A tick on the screen: the whole row is the box's label, the box drawn as Tripmo's (a to-do's done is moss), the
// real checkbox inside it for the keyboard and screen readers. A link in the words opens without ticking.
function Tick({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <label className={`tm-tick${checked ? " is-on" : ""}`} htmlFor={id}>
      <span className="tm-tick-box" aria-hidden="true">
        <Icon name="check" size={18} strokeWidth={2.5} />
      </span>
      <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="body tm-tick-text">{children}</span>
    </label>
  );
}
